<template>
  <v-container fluid class="pa-4">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="title">{{ $t('title.zaposlenici') }}</span>
        <div class="d-flex">
          <v-tooltip location="bottom" content-class="custom-tooltip">
            <template #activator="{ props }">
              <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="mr-2 action-btn"
                icon="mdi-eye" @click="printUsers">
              </v-btn>
            </template>
            {{ $t('general.viewprint') }}
          </v-tooltip>

          <v-tooltip location="bottom" content-class="custom-tooltip">
            <template #activator="{ props }">
              <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="action-btn" icon="mdi-plus"
                type="button" @click="addUser" />
            </template>

            {{ $t('general.addUser') }}
          </v-tooltip>
        </div>
      </v-card-title>

      <v-card-text>
        <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" class="mb-2" autocomplete="off"
          density="compact" />

        <v-data-table :headers="headers" :items="filteredUsers" :loading="loading" :loading-text="$t('general.loading')"
          class="min-width-table custom-header" :items-per-page="15">

          <!-- Bold naslovi i sort ikonice -->
          <template v-slot:headers="{ columns, toggleSort, isSorted, getSortIcon }">
            <tr>
              <th v-for="column in columns" :key="column.value" @click="() => column.sortable && toggleSort(column)"
                :style="{
                  cursor: column.sortable ? 'pointer' : 'default',
                  width: column.width,
                  maxWidth: column.width,
                  textAlign: column.value === 'actions' ? 'right' : 'left'
                }" class="sortable-header">
                <div class="d-flex align-center w-100" :class="column.value === 'actions'
                  ? 'justify-end'
                  : 'justify-space-between'">
                  <strong>{{ column.text }}</strong>

                  <v-icon v-if="column.sortable" size="12" class="sort-icon ml-2" :class="{ active: isSorted(column) }">
                    {{ isSorted(column) ? getSortIcon(column) : 'mdi-arrow-up-down' }}
                  </v-icon>
                </div>
              </th>
            </tr>
          </template>

          <!-- Akcije -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex justify-end action-buttons">

              <!-- EDIT -->
              <v-tooltip location="bottom" content-class="custom-tooltip">
                <template #activator="{ props }">
                  <v-btn v-bind="props" variant="outlined" color="primary" class="mr-2 action-btn" type="button"
                    @click="editUser(item)">
                    <v-icon size="16">mdi-pencil</v-icon>
                  </v-btn>
                </template>
                {{ $t('general.uredi') }}
              </v-tooltip>

              <!-- POBRISI-->
              <v-tooltip location="bottom" content-class="custom-tooltip">
                <template #activator="{ props }">
                  <v-btn v-bind="props" size="small" variant="outlined" color="red" class="action-btn" type="button"
                    @click="openDeleteDialog(item)">
                    <v-icon size="16">mdi-delete</v-icon>
                  </v-btn>
                </template>
                {{ $t('general.delete') }}
              </v-tooltip>
            </div>
          </template>

        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dijalog za dodavanje/uređivanje zaposlenika -->
    <BaseModal v-model="dialog" :title="editMode ? this.$t('modalni.user.uredi') : this.$t('modalni.user.dodaj')">
      <v-form ref="userPageForm" v-model="isFormValid">
        <v-text-field v-model="form.Name" :label="$t('table.zaposlenici.ime')" :rules="[rules.required]" class="my-text"
          required />
        <v-text-field v-model="form.Email" :label="$t('table.zaposlenici.email')" type="email" :rules="[rules.email]"
          class="my-text" />

        <v-select v-model="form.WorkPositionId" :items="positions" :label="$t('table.zaposlenici.pozicija')"
          item-title="Name" item-value="Id" :rules="[rules.required]" class="my-selectModal" required />

        <v-select v-model="form.DepartmentId" :items="departments" :label="$t('table.zaposlenici.orgJedinica')"
          item-title="Name" item-value="Id" :rules="[rules.required]" class="my-selectModal" required />
      </v-form>

      <template #footer>
        <div class="d-flex justify-end ga-2">
          <!-- OTKAŽI -->
          <v-btn variant="outlined" @click="closeDialog">
            <span style="font-size:14px; font-weight: 600;">{{ $t('general.cancel') }}</span>
          </v-btn>

          <!-- SAČUVAJ -->
          <v-btn color="primary" variant="flat" @click="saveUser">
            <span style="font-size:14px; font-weight: 600;">{{ $t('general.save') }}</span>
          </v-btn>
        </div>
      </template>

    </BaseModal>

    <!-- Dijalog za potvrdu brisanja zaposlenika -->
    <BaseModal v-model="deleteDialog" :title="this.$t('modalni.user.brisi')">
      <p>{{ $t('User_message.m87') }} <strong>{{ deleteUserName }}</strong>?</p>
      <p>{{ $t('User_message.m126') }}</p>
      <template #footer>
        <div class="d-flex justify-end ga-2">
          <!-- OTKAŽI -->
          <v-btn variant="outlined" @click="deleteDialog = false">
            <span style="font-size:14px; font-weight: 600;">{{ $t('general.cancel') }}</span>
          </v-btn>

          <!-- OBRIŠI -->
          <v-btn color="danger" variant="flat" @click="confirmDelete">
            <span style="font-size:14px; font-weight: 600; color: #FFF">{{ $t('general.deleteButton') }}</span>
          </v-btn>
        </div>
      </template>
    </BaseModal>

    <!--Save/Print dialog-->
    <BaseModal v-model="reportPreview" size="xl" :title="$t('print.listaZaposlenika')" height="90vh"
      modalClass="report-modal" :hideFooter="true">

      <template #header-actions>

        <v-tooltip location="bottom" content-class="custom-tooltip" :text="$t('general.download')">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon variant="plain" @click="downloadPDF">
              <v-icon size="18"
                style="padding-top: 2px !important; padding-left: 10px !important;">mdi-download</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

        <v-tooltip location="bottom" content-class="custom-tooltip" :text="$t('general.print')">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon variant="plain" @click="printReport">
              <v-icon size="18">mdi-printer</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

      </template>

      <iframe ref="reportFrame" :style="{
        width: '100%',
        height: '100%',
        border: 'none',
        transform: `scale(${zoom})`,
        transformOrigin: 'top left'
      }" />

    </BaseModal>

  </v-container>
</template>

<script>
import { printTable, fetchHeaderImage } from "@/services/printService";
import BaseModal from "@/components/dialogs/BaseModal.vue";
import html2pdf from "html2pdf.js"
import { format } from "date-fns";

export default {
  name: "UsersPage",
  data() {
    return {
      html: '',
      zoom: 1,
      reportPreview: false,
      deleteDialog: false,
      deleteUserId: null,
      deleteUserName: "",
      search: "",
      loading: false,
      dialog: false,
      editMode: false,
      Id: null,
      users: [],
      departments: [],
      positions: [],
      isFormValid: false,
      rules: {
        required: (v) => !!v || this.$t('systemMessage.m1'),
        email: (v) => !v || /.+@.+\..+/.test(v) || this.$t('systemMessage.m3'),
      },

      form: {
        Name: "",
        WorkPosition: null,
        WorkPositionId: null,
        Department: null,
        DepartmentId: null
      },

      headers: [
        { text: this.$t('table.zaposlenici.ime'), value: "Name", sortable: true, width: '20%' },
        { text: this.$t('table.zaposlenici.email'), value: "Email", sortable: true, width: '20%' },
        { text: this.$t('table.zaposlenici.pozicija'), value: "WorkPosition", sortable: true, width: '20%' },
        { text: this.$t('table.zaposlenici.orgJedinica'), value: "Department", sortable: true, width: 'calc(40% - 100px)' },
        { text: this.$t('general.optionsUPPER'), value: "actions", sortable: false, width: '100px' },
      ],
      CurrentLanguageId: null

    };
  },
  components: {
    BaseModal,
  },

  computed: {
    filteredUsers() {
      const term = this.search.toLowerCase().trim();

      const filtered = this.users.filter(u =>
        Object.values(u).some(val =>
          String(val ?? "").toLowerCase().includes(term)
        )
      );
      return filtered;
    },
  },

  async mounted() {
    await this.fetchDepartments();
    await this.fetchPositions();
    await this.fetchUsers();
  },

  methods: {
    generateReportFileName(title) {

      const date = new Date()

      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()

      const compactDate = `${day}${month}${year}`

      const cleanTitle = title
        .replace(/[čć]/g, "c")
        .replace("ž", "z")
        .replace("š", "s")
        .replace("đ", "d")
        .split(" ")
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join("")

      return `${cleanTitle}_${compactDate}.pdf`
    },
    openReportPreview(html) {

      this.reportPreview = true

      this.$nextTick(() => {
        this.$nextTick(() => {

          const iframe = this.$refs.reportFrame

          if (!iframe) return

          const doc = iframe.contentWindow.document

          doc.open()
          doc.write(html)
          doc.close()

        })
      })
    },

    printReport() {
      const iframe = this.$refs.reportFrame
      iframe.contentWindow.print()
    },


    downloadPDF() {

      const container = document.createElement("div")
      container.innerHTML = this.html

      const title = container.querySelector("h2")?.innerText

      const fileName = this.generateReportFileName(title)

      html2pdf()
        .set({
          margin: 10,
          filename: fileName,
          html2canvas: { scale: 2 },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "landscape"
          }
        })
        .from(container)
        .save()
    },
    async printUsers() {
      const users = this.filteredUsers || []

      if (!users.length) {
        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.$t("print.na"),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550",
        })
        return
      }

      const headerImg = await fetchHeaderImage(this.$axios)

      this.html = printTable({
        title: this.$t("print.listaZaposlenika"),
        columns: [
          { key: "name", label: this.$t("table.zaposlenici.ime"), width: "20%" },
          { key: "email", label: this.$t("table.zaposlenici.email"), width: "20%" },
          { key: "position", label: this.$t("table.zaposlenici.pozicija"), width: "25%" },
          { key: "department", label: this.$t("table.zaposlenici.orgJedinica"), width: "35%" },
        ],
        data: users.map(u => ({
          name: u.Name ?? "-",
          email: u.Email ?? "-",
          position: u.WorkPosition ?? "-",
          department: u.Department ?? "-",
        })),
        headerImg,
        footer: `${this.$t("exportToExcel.pokrenuo")} ${localStorage.getItem("Name")} <br />
    ${this.$t("print.m1")} ${format(new Date(), "dd.MM.yyyy.")}`,
      })
      this.openReportPreview(this.html)
    },
    openDeleteDialog(user) {
      this.deleteUserId = user.UserId;
      this.deleteUserName = user.Name;
      this.deleteDialog = true;
    },

    cancelDelete() {
      this.deleteDialog = false;
      this.deleteUserId = null;
      this.deleteUserName = "";
    },

    async confirmDelete() {
      if (!this.deleteUserId) return;

      try {
        const r = await this.$axios.put(
          'User/Delete/' + this.deleteUserId
        );

        if (r.status === 200) {
          this.$store.dispatch("NOTIF_SUCCESS", {
            poruka: this.$t('User_message.m82'),
            position: "top-end",
            timer: 2500,
            action: "noreload"
          });

          await this.fetchUsers();
        }

      } catch (err) {
        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.$t('User_message.m83'),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550"
        });
      } finally {
        this.cancelDelete();
      }
    },

    async fetchPositions() {
      this.loading = true;
      try {
        var data = {
          Id: Number(localStorage.getItem('CompanyId')),
          LanguageId: Number(localStorage.getItem('LanguageId'))
        }
        const r = await this.$axios.post('Role/WorkPosition/', data)

        this.positions = r.data

      } catch (err) {
        console.error(this.$t('general.noData'), err)
        this.positions = []
      } finally {
        this.loading = false;
      }
    },

    async fetchUsers() {
      this.loading = true;
      try {
        var data = {
          Id: Number(localStorage.getItem('CompanyId')),
          LanguageId: Number(localStorage.getItem('LanguageId'))
        }

        const r = await this.$axios.post('User/Show/', data)

        this.users = r.data

      } catch (err) {
        console.error(this.$t('general.noData'), err)
        this.users = []
      } finally {
        this.loading = false;
      }
    },

    async fetchDepartments() {
      this.loading = true;
      try {
        var data = {
          Id: Number(localStorage.getItem('CompanyId')),
          LanguageId: Number(localStorage.getItem('LanguageId'))
        }

        const r = await this.$axios.post('Role/Department/', data)

        this.departments = r.data;

      } catch (err) {
        console.error(this.$t('general.noData'), err)
        this.departments = []
      } finally {
        this.loading = false;
      }
    },

    addUser() {
      this.editMode = false;
      this.selectedId = null;
      this.resetForm();
      this.dialog = true;

      this.$nextTick(() => {
        this.$refs.userPageForm?.resetValidation()
      })
    },

    editUser(user) {
      if (!this.positions.some(x => x.Id === user.WorkPositionId)) {
        user.WorkPositionId = '';
      }

      if (!this.departments.some(x => x.Id === user.DepartmentId)) {
        user.DepartmentId = '';
      }

      this.editMode = true;
      this.selectedId = user.UserId;
      this.form = { ...user };
      this.dialog = true;

      this.$nextTick(() => {
        this.$refs.userPageForm?.resetValidation()
      })
    },

    async saveUser() {
      const { valid } = await this.$refs.userPageForm.validate()
      if (!valid) return

      try {
        if (this.editMode) {
          const data = {
            Id: this.selectedId,
            Name: this.form.Name,
            Email: this.form.Email,
            WorkPositionId: this.form.WorkPositionId,
            DepartmentId: this.form.DepartmentId,
            CompanyId: Number(localStorage.getItem('CompanyId'))
          };

          const r = await this.$axios.put('User/Edit/', data);

          if (r.data === "UPDATED") 
          {
            this.$store.dispatch("NOTIF_SUCCESS", {
              poruka: this.$t('User_message.m23'),
              position: "top-end",
              timer: 2500,
              action: "noreload"
            });
          } 
          else 
          {
            this.$store.dispatch("NOTIF_WARNING", {
              poruka: this.$t('User_message.m24'),
              position: "center",
              icon: "error",
              timer: 4500,
              width: "550"
            });
          }

        } else {

          if(localStorage.getItem('RoleId') == 1) //Ako je SA
          {
            if(localStorage.getItem('CountryId') == 1)
            {
              this.CurrentLanguageId = 1
            }
            if(localStorage.getItem('CountryId') == 2)
            {
              this.CurrentLanguageId = 3
            }
            if(localStorage.getItem('CountryId') == 3)
            {
              this.CurrentLanguageId = 4
            }
          }
          else
          {
            this.CurrentLanguageId = Number(localStorage.getItem('LanguageId'))
          }

          const data = {
            Name: this.form.Name,
            Email: this.form.Email == "" ? null : this.form.Email,
            WorkPositionId: this.form.WorkPositionId,
            DepartmentId: this.form.DepartmentId,
            CompanyId: Number(localStorage.getItem('CompanyId')),
            CountryId: Number(localStorage.getItem('CountryId')),
            LanguageId: this.CurrentLanguageId
          };

          const r = await this.$axios.post('User/Add/', data);

          if (r.data === "INSERTED") {
            this.$store.dispatch("NOTIF_SUCCESS", {
              poruka: this.$t('User_message.m21'),
              position: "top-end",
              timer: 2500,
              action: "noreload"
            });
          } else if (r.data === "EXISTS EMAIL") {
            this.$store.dispatch("NOTIF_WARNING", {
              poruka: this.$t("User_message.m152"),
              position: "center",
              icon: "error",
              timer: 4500,
              width: "550"
            });
            return
          } else {
            this.$store.dispatch("NOTIF_WARNING", {
              poruka: this.$t('User_message.m22'),
              position: "center",
              icon: "error",
              timer: 4500,
              width: "550"
            });
          }
        }

        this.dialog = false;
        this.fetchUsers();

      } catch (err) {
        console.error(err);
      }
    },
    closeDialog() {
      this.dialog = false;
      this.resetForm();

      this.$nextTick(() => {
        this.$refs.userPageForm?.resetValidation()
      })
    },
    resetForm() {
      this.form = {
        Name: "",
        Email: "",
        WorkPositionId: null,
        DepartmentId: null
      };
      this.selectedId = null;
    },
  },
};
</script>
